import type { Dispatch, SetStateAction } from "react";

import type { IDraggableItem } from "@similarweb/ui-components";

import { chosenSites } from "app/common/services/chosenSites";
import type { WebsitePageSearchType } from "pro-features/components/AutocompleteWebsites/types";
import {
    getDomainWithSubdomain,
    getUrlComponents,
} from "pro-features/components/AutocompleteWebsites/urlComponentUtils";
import { transformSiteListToParam } from "pro-features/components/AutocompleteWebsites/utils";
import { CHART_COLORS } from "app/constants/ChartColors";
import { translate } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { allTrackers } from "app/services/track/track";

interface IChosenItemCreation {
    url: string;
    icon: string;
    image: string;
    favicon: string;
    color: string;
    searchType?: WebsitePageSearchType;
}

export interface IChosenItem extends IChosenItemCreation {
    searchType: WebsitePageSearchType;
    domain?: string;
    subdomain?: string;
    page?: string;
    queryParams?: string;

    /** for compatibility with website query bar */
    displayName: string;
    /** for compatibility with website query bar */
    name: string;
    /** for compatibility with website query bar */
    smallIcon: false;
}

type IChosenItemParam = Pick<IChosenItem, "url" | "searchType">;
export type IChosenItemParams = IChosenItemParam[];

export const transformSite = (
    item: { domain: string; iconUrl: string },
    searchType: WebsitePageSearchType,
): Pick<IChosenItem, "url" | "searchType" | "icon"> => {
    const { subdomain } = getUrlComponents(item.domain);
    return {
        url: item.domain,
        icon: item.iconUrl,
        searchType: searchType ?? (subdomain ? "subdomain" : "domain"),
    };
};

export const getQueryBarSecondaryTextMap = (): Record<WebsitePageSearchType, string> => ({
    domain: translate("querybar.websitePages.secondaryText.domain"),
    subdomain: translate("querybar.websitePages.secondaryText.subdomain"),
    folder: translate("querybar.websitePages.secondaryText.page"),
    exact: translate("querybar.websitePages.secondaryText.url"),
});

export const createChosenItem = (item: IChosenItemCreation): IChosenItem => {
    const { domain, subdomain, folder, queryParams } = getUrlComponents(item.url);
    const domainWithSubdomain = getDomainWithSubdomain(domain ?? "", subdomain ?? "");
    return {
        searchType: item.searchType ?? (subdomain ? "subdomain" : "domain"),
        domain,
        subdomain,
        page: folder,
        queryParams,
        url: item.url,
        icon: item.icon,
        image: item.image,
        favicon: item.favicon,
        color: item.color,
        smallIcon: false,
        displayName: domainWithSubdomain,
        name: domainWithSubdomain,
    };
};

export const getChosenPageItems = (urlList: IChosenItemParams) => {
    const domainList = urlList.map((page) => page.url.split("/")[0]);
    const sitesData = domainList.map((site, index) => {
        const siteInfo = chosenSites.getInfo(site);
        return createChosenItem({
            url: urlList[index].url,
            icon: siteInfo?.icon,
            image: siteInfo?.image,
            favicon: siteInfo?.favicon,
            color: CHART_COLORS.compareMainColors[index],
            searchType: urlList[index].searchType,
        });
    });
    return sitesData;
};

export const changeQueryBarOrder = (
    newColumns: IDraggableItem[],
    chosenItems: IChosenItem[],
    listItemOrder: string[],
    setListItemOrder: Dispatch<SetStateAction<string[]>>,
) => {
    const reorderedColumnIds = newColumns.flatMap((column) => column.itemsIds ?? column.id);
    const didOrderChange = reorderedColumnIds.some((item, index) => listItemOrder[index] !== item);

    allTrackers.dispatchTrackEvent("query-bar.website.page.drag.end", "click", "query-bar.website.page.drag.end");

    if (didOrderChange) {
        setListItemOrder(reorderedColumnIds);
        const chosenItemsSorted = reorderedColumnIds
            .map((id) => chosenItems.find((item) => item.url === id))
            .filter((item): item is IChosenItem => item !== undefined);
        const pageFilter = transformSiteListToParam(chosenItemsSorted);
        // take only the subdomain.domain and remove duplicates
        const key = Array.from(new Set(chosenItemsSorted.map((item) => item.displayName)));
        proNavigator.updateParams({ key: key.join(), pageFilter }, {});
    }
};
